import useModalWithContext from '../../../../hooks/useModalWithContext';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../typings/api';
import type { ModalStateWithContext } from '../../../../hooks/useModalWithContext';

import { type ManagePackageLabelsState, useManagePackageLabelsModal } from './useManagePackageLabelsModal';

type AddDependencyModalState = {
  // is the user adding a `new` dependency, or `replace`-ing an unknown/unresolved one
  mode?: ManuallyAddedDependencyRequestKind;
  // dependency being overwritten
  dependencyToOverwrite?: string;
};

type EditDependency = {
  revision?: string;
};

export type StreamBuildModalState = {
  buildId?: number;
};

type ViewRelationshipsModalState = {
  revision?: string;
  title?: string;
};

type DeleteDependencyModalState = {
  revision?: string;
  title?: string;
  isManual?: boolean;
};

type ViewBinaryMatchDetailsModalState = {
  revision?: string;
};

export type DependencyV2Modals = {
  addDependencyModal: ModalStateWithContext<AddDependencyModalState>;
  editDependencyModal: ModalStateWithContext<EditDependency>;
  managePackageLabelsModal: ModalStateWithContext<ManagePackageLabelsState>;
  deleteDependencyModal: ModalStateWithContext<DeleteDependencyModalState>;
  streamBuildModal: ModalStateWithContext<StreamBuildModalState>;
  viewRelationshipsModal: ModalStateWithContext<ViewRelationshipsModalState>;
  viewBinaryMatchDetailsModal: ModalStateWithContext<ViewBinaryMatchDetailsModalState>;
};

/**
 * This is a wrapper hook to provide all the modals for the dependency page.
 * This will contain multiple modals in the future.
 * @returns {DependencyV2Modals} modal contexts
 */
const useDependencyModals = (): DependencyV2Modals => {
  const addDependencyModal = useModalWithContext<AddDependencyModalState>({});
  const editDependencyModal = useModalWithContext<EditDependency>({});
  const managePackageLabelsModal = useManagePackageLabelsModal();
  const deleteDependencyModal = useModalWithContext<DeleteDependencyModalState>({});
  const streamBuildModal = useModalWithContext<StreamBuildModalState>({});
  const viewRelationshipsModal = useModalWithContext<ViewRelationshipsModalState>({});
  const viewBinaryMatchDetailsModal = useModalWithContext<ViewBinaryMatchDetailsModalState>({});

  return {
    addDependencyModal,
    editDependencyModal,
    managePackageLabelsModal,
    deleteDependencyModal,
    streamBuildModal,
    viewRelationshipsModal,
    viewBinaryMatchDetailsModal,
  };
};

export default useDependencyModals;
